/* eslint-disable no-console */
import { FC, MouseEvent, useEffect, useMemo, useState } from 'react';
import { nanoid } from 'nanoid';
import { CardCvcElement, CardExpiryElement, CardNumberElement, PaymentRequestButtonElement, useElements, useStripe } from '@stripe/react-stripe-js';
import {
  PaymentRequest,
  PaymentRequestPaymentMethodEvent,
  StripeCardCvcElementChangeEvent,
  StripeCardExpiryElementChangeEvent,
  StripeCardNumberElementChangeEvent,
  StripeError,
} from '@stripe/stripe-js';
import Bluebird from 'bluebird';
import createPaymentIntent from '@tsClient/createPaymentIntent';
import createSubscription from '@tsClient/createStripeSubscriptions';
import { useRouter } from 'next/router';
import { getUrl, IDENTIFIERS } from '@helpers/urlsHelper';
import Spacer from '@components/Spacer';
import useCart from '@hooks/useCart';
import ShoppingCart, { CartItem } from '@providers/ShoppingCart';
import { calculateTotalPriceInCents, CARD_ELEMENT_OPTIONS } from '@helpers/checkout';
import getPrimaryProductId from '@helpers/getPrimaryProductId';
import * as Types from '@typings/graphql-models';
import { NoodleProductTypes } from '@typings/graphql-models';
import retry from 'promise-retry';
import { getUTMParams } from '@helpers/analytics';
import useNoodleApi from '@hooks/useNoodleApi';
import { logError, Severity } from '@providers/ErrorTracking';
import { useGAClientId } from '@providers/GoogleAnalytics';
import { mixpanelTrack } from '@providers/Mixpanel';
import useLocalCurrency from '@hooks/useLocalCurrency';
import * as tsClient from '@tsClient';
import { attachNewCard, getPersonProductsByProductId } from '@tsClient';
import { NEXT_PUBLIC_FEATURE_DEBUG_AUTOFILL_ENABLED } from '@configuration/client';
import ProgressIndicator from '@components/ProgressIndicator';
import Buttons from '@components/Buttons';
import PlusCircle from '@components/Icons/PlusCircle';
import ArrowCircleLeft from '@components/Icons/ArrowCircleLeft';
import classNames from 'classnames';

import ApiError from '@helpers/ApiError';
import InputField from '@components/FormFields/InputField';
import debounce from 'lodash/debounce';
import { useUser, useUserProfile } from '@providers/Auth';
import removeNullish from '@helpers/removeNullish';
import * as stripeHelpers from '@helpers/stripe';
import hasNoodleProductType from '@helpers/hasNoodleProductType';
import deserializeQueryStringItem from '@helpers/deserializeQueryStringItem';
import { SystemHealthMonitor } from '@providers/SystemHealth';
import Tag from '@components/Icons/Tag';
import { format } from 'date-fns';
import classes from './cartPaymentFormStep.module.scss';
import isFreePrice from './isFreePrice';
import isOneTimePrice from './isOneTimePrice';
import isSubscriptionPrice from './isSubscriptionPrice';
import isButtonEnabled from './isButtonEnabled';
import canAddNewPaymentMethod from './canAddNewPaymentMethod';
import isCreditCardRequired from './isCreditCardRequired';
import DefaultPaymentMethod from './DefaultPaymentMethod';
import PersonalInfo from './PersonalInfo';

type ThisCreator = Pick<Types.Creator, 'stripe_account_id' | 'name' | 'id' | 'stripeAccountType' | 'personId'>;
type Props = {
  creatorSlug: string;
  creator: ThisCreator;
  customTermsId?: string;
  isSaaSProduct?: boolean;
  tierTitle?: string;
  sessionDuration?: number;
  sessionTime?: string;
  personId: string | null;
  initialEmail: string | null;
  initialName: string | null;
  onChangeBookingTime?: () => void;
};

type PaymentSuccess = {
  success: boolean;
  error?: StripeError | string;
  hadItemsToPurchase: boolean;
};

const onlyOneTimePurchaseItems = (cart: CartItem[] | null): CartItem[] => (cart || []).filter(price => isOneTimePrice(price) && !isFreePrice(price));

const CartPaymentFormStep: FC<Props> = ({
  creatorSlug,
  creator,
  customTermsId,
  initialEmail,
  initialName,
  isSaaSProduct,
  tierTitle,
  sessionDuration,
  sessionTime,
  personId: existingPersonId,
  onChangeBookingTime,
}) => {
  const [succeeded, setSucceeded] = useState(false);
  const [error, setError] = useState<StripeError | string | null>(null);
  const [paymentRequest, setPaymentRequest] = useState<PaymentRequest | null>(null);
  const [processing, setProcessing] = useState(false);
  const localCurrency = useLocalCurrency();
  const [user, _, setUserByToken] = useUser();
  const profile = useUserProfile();
  const stripe = useStripe();
  const elements = useElements();
  const router = useRouter();
  const { cart, discountCode: appliedDiscountCode } = useCart({ creatorSlug });
  const gaClientId = useGAClientId();
  const { getData: createSubscriptionsFn } = useNoodleApi(createSubscription, { reportError: false });
  const { getData: checkDiscountCodeFn } = useNoodleApi(tsClient.checkDiscountCode);
  const [discountCode, setDiscountCode] = useState(appliedDiscountCode?.code || '');
  const [discountError, setDiscountError] = useState<string | null>(null);

  const { getData: getPersonProductsByProductIdFn } = useNoodleApi(getPersonProductsByProductId);
  const { getData: buyFreeProductFn } = useNoodleApi(tsClient.buyFreeProduct);
  const { getData: findOrCreatePerson } = useNoodleApi(tsClient.findOrCreatePerson);
  const { data: myPaymentMethodsResponse, getData: getMyPaymentMethodsFn } = useNoodleApi(tsClient.paymentMethods.getMyPaymentMethods);
  const { getData: createSetupIntentFn } = useNoodleApi(tsClient.createSetupIntent);
  const { data: thisCustomer, getData: getCustomerStripeFn } = useNoodleApi(tsClient.getCustomerStripe);
  const { getData: createPaymentRequest } = useNoodleApi(tsClient.payments.createPaymentRequest);
  const [email, setEmail] = useState<string>(initialEmail || '');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [hasInitialEmail, setHasInitialEmail] = useState(Boolean(initialEmail));
  const [hasInitialName, setHasInitialName] = useState(Boolean(initialName));
  const [name, setName] = useState<string>(initialName || '');
  const [payerName, setPayerName] = useState<string>(initialName || '');
  const [isCardPaymentEnabled, setCardPaymentEnabled] = useState(true);
  const [isAchPaymentEnabled, setAchPaymentEnabled] = useState(false);
  const [onlyFreeProducts, setOnlyFreeProducts] = useState(false);
  const [cartHasProductsWithSchedule, setCartHasProductsWithSchedule] = useState(false);
  const [isValidCCNumber, setIsValidCCNumber] = useState(false);
  const [isValidExpiry, setIsValidExpiry] = useState(false);
  const [isValidCvc, setIsValidCvc] = useState(false);
  const [paymentMethodEvent, setPaymentMethodEvent] = useState<PaymentRequestPaymentMethodEvent | null>(null);
  const [isFetchingPaymentMethod, setIsFetchingPaymentMethod] = useState(false);
  const [useDifferentPaymentMethod, setUseDifferentPaymentMethod] = useState(false);
  const [userHasCustomerStripeId, setUserHasCustomerStripeId] = useState(false);
  const [showDiscountCodeInput, setShowDiscountCodeInput] = useState(false);
  const [isRouting, setIsRouting] = useState(false);
  const [shouldPrefillPayerName, setShouldPrefillPayerName] = useState(true);
  const errorCorrelationId = useMemo(() => nanoid(), []);

  const myPaymentMethods = myPaymentMethodsResponse?.items || [];
  const customerDefaultPaymentMethodId = thisCustomer?.invoice_settings?.default_payment_method || null;
  const unverifiedPaymentMethods = myPaymentMethods.filter(pm => Boolean(pm.verifyWithMicrodeposits));
  const hasUnverifiedPaymentMethods = unverifiedPaymentMethods.length > 0;
  const defaultPaymentMethod = myPaymentMethods.find(p => p.id === customerDefaultPaymentMethodId) || null;
  const isAddNewPaymentMethodButtonShown = canAddNewPaymentMethod({
    creatorSlug,
    defaultPaymentMethod,
    isAchPaymentEnabled,
    isCardPaymentEnabled,
    useDifferentPaymentMethod,
    userHasCustomerStripeId,
  });
  const freeSchedulingPrice = cart.find(
    price => hasNoodleProductType(price.product, NoodleProductTypes.InPersonSession, NoodleProductTypes.OnlineSession) && isFreePrice(price),
  );
  const needsSessionInfo = Boolean(freeSchedulingPrice?.sessionDuration);
  const onlyFreeScheduling = needsSessionInfo && cart.length === 1;

  const needsEmail = !user || user.isAnonymous;
  const userId = user?.id;
  const creatorId = creator.id;
  const useDefaultPaymentMethod = defaultPaymentMethod && !useDifferentPaymentMethod;

  const workflowReferenceId = deserializeQueryStringItem(router.query.workflowReferenceId, { type: 'string' });

  const userIsCreator = userId === creator.personId;

  const disableButton = !isButtonEnabled({
    defaultPaymentMethod,
    elements,
    emailError,
    hasUnverifiedPaymentMethods,
    isAchPayment: isAchPaymentEnabled,
    isValidCCNumber,
    isValidCvc,
    isValidExpiry,
    name,
    needsEmail,
    onlyFreeProducts,
    payerName,
    processing,
    stripe,
    succeeded,
    useDifferentPaymentMethod,
    userIsCreator,
  });

  const showCreditCardForm = isCardPaymentEnabled
    && isCreditCardRequired({
      defaultPaymentMethod,
      onlyFreeProducts,
      useDifferentPaymentMethod,
    });

  useEffect(() => {
    if (profile?.email) {
      setEmail(profile.email);
      setHasInitialEmail(true);
    }
    if (profile?.name) {
      setName(profile.name);
      setHasInitialName(true);
      if (!payerName) {
        setPayerName(profile?.name);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  useEffect(() => {
    if (shouldPrefillPayerName) {
      setPayerName(name);
    }
  }, [name, setPayerName, shouldPrefillPayerName]);

  useEffect(() => {
    // Need the === false since existing carts pulled out of localStorage won't have isCardPaymentEnabled
    setCardPaymentEnabled(!cart.find(p => p.isCardPaymentEnabled === false));
    setAchPaymentEnabled(!cart.find(p => !p.isAchPaymentEnabled));
  }, [cart]);

  const handleSubmitText = (): string => {
    if (cartHasProductsWithSchedule) {
      return onlyFreeScheduling ? 'Finish' : 'Finish & Pay';
    }
    if (onlyFreeProducts) {
      return 'Get for free';
    }
    if (isAchPaymentEnabled && !isCardPaymentEnabled && defaultPaymentMethod?.us_bank_account && !useDifferentPaymentMethod) {
      return 'Pay via ACH';
    }
    if (isAchPaymentEnabled && !isCardPaymentEnabled) {
      return 'Connect bank account';
    }
    return 'Finish & Pay';
  };

  const handleCardElementChange = async (event: StripeCardNumberElementChangeEvent): Promise<void> => {
    if (NEXT_PUBLIC_FEATURE_DEBUG_AUTOFILL_ENABLED) {
      console.log('handleCardElement', event); // eslint-disable-line no-console
    }
    setIsValidCCNumber(event.complete);
  };

  const handleExpiryChange = async (event: StripeCardExpiryElementChangeEvent): Promise<void> => {
    setIsValidExpiry(event.complete);
  };

  const handleCvcChange = async (event: StripeCardCvcElementChangeEvent): Promise<void> => {
    setIsValidCvc(event.complete);
  };

  useEffect(() => {
    const isFree = cart.every(isFreePrice);
    const hasSchedule = cart.some(
      p => hasNoodleProductType(p.product, Types.NoodleProductTypes.InPersonSession, Types.NoodleProductTypes.OnlineSession) && p.sessionDuration,
    );
    if (!processing) {
      setOnlyFreeProducts(isFree);
      setCartHasProductsWithSchedule(hasSchedule);
    }
  }, [cart, processing]);

  const applyDiscountCode = useMemo(
    () =>
      debounce(async (code: string): Promise<void> => {
        if (code && cart.length > 0) {
          const { data, error: discountCodeError } = await checkDiscountCodeFn({ discountCode: code, priceIds: cart.map(p => p.id) });
          if (data) {
            setDiscountError(null);
            ShoppingCart.setDiscountCode(data);
          }
          if (discountCodeError && code) {
            ShoppingCart.setDiscountCode(null);
            if (discountCodeError.type === 'InvalidDiscountCodeError') {
              const errorMessage = discountCodeError.errors?.[0];
              if (errorMessage === 'Does not meet minimum purchase') {
                setDiscountError(errorMessage);
              } else {
                setDiscountError('Invalid discount code. Try again.');
              }
            } else if (discountCodeError.type === 'NotApplicableDiscountCodeError') {
              setDiscountError('Code is not valid for products in your cart. Try again.');
            } else {
              setDiscountError('Failed to set discount code. Try again.');
            }
          }
        } else {
          setDiscountError(null);
          ShoppingCart.setDiscountCode(null);
        }
      }, 400),
    [cart, checkDiscountCodeFn],
  );

  useEffect(() => {
    applyDiscountCode(discountCode);
  }, [applyDiscountCode, discountCode]);

  useEffect(() => {
    const getPaymentMethods = async (): Promise<void> => {
      try {
        if (creatorId && userId && !user.isAnonymous) {
          setIsFetchingPaymentMethod(true);
          const getCustomerResponse = await getCustomerStripeFn({ creatorId });
          await getMyPaymentMethodsFn({ creatorId });

          setUserHasCustomerStripeId(Boolean(getCustomerResponse.data?.id));
          setIsFetchingPaymentMethod(false);
        }
      } catch (err) {
        setUserHasCustomerStripeId(false);
        setIsFetchingPaymentMethod(false);
      }
    };
    getPaymentMethods();
  }, [user, userId, creatorId, getMyPaymentMethodsFn, getCustomerStripeFn]);

  useEffect(() => {
    if (stripe) {
      const pr = stripe.paymentRequest({
        country: 'US',
        currency: cart?.[0]?.currency?.toLowerCase() || 'usd',
        requestPayerEmail: true,
        requestPayerName: true,
        total: {
          amount: calculateTotalPriceInCents(cart.map(price => price.price)),
          label: 'Noodle',
        },
      });
      pr.canMakePayment().then(result => {
        if (result) {
          setPaymentRequest(pr);
        }
      });
    }
  }, [cart, stripe]);

  const purchaseOneTimeItems = async (paymentMethodId: string, personId: string): Promise<PaymentSuccess> => {
    if (!creator.stripe_account_id) {
      throw new Error('Expert is not on stripe');
    }

    const oneTimeItems = onlyOneTimePurchaseItems(cart);

    if (oneTimeItems.length === 0) {
      return { hadItemsToPurchase: false, success: true };
    }

    const paymentIntent = await createPaymentIntent({
      accountId: creator.stripe_account_id,
      customTermsId: customTermsId || null,
      discountCodeId: appliedDiscountCode?.id || null,
      gaClientId,
      localCurrency,
      paymentMethodId,
      personId,
      prices: oneTimeItems,
      sessionDuration,
      sessionTime,
      statementDescriptor: creatorSlug,
      utmParams: {
        ...getUTMParams(router.query),
      },
      workflowReferenceId,
    });

    const clientSecret = paymentIntent.client_secret;
    if (!clientSecret) {
      throw new Error('No clientSecret returned with payment intent');
    }

    if (!stripe || !elements) {
      console.warn('errorCorrelationId', errorCorrelationId);
      mixpanelTrack('Error in checkout flow (no stripe instance OTP)', { errorCorrelationId });
      logError(new Error('No stripe instance'), { errorCorrelationId, level: Severity.Fatal, tags: { flow: 'purchase' } });
      return { error: 'No stripe instance', hadItemsToPurchase: true, success: false };
    }

    let payload;

    if (isCardPaymentEnabled) {
      payload = await stripe.confirmCardPayment(clientSecret, {
        payment_method: paymentMethodId,
      });
    } else if (isAchPaymentEnabled) {
      payload = await stripe.confirmUsBankAccountPayment(clientSecret, {
        payment_method: paymentMethodId,
      });
    }
    if (payload?.error) {
      console.log('info', payload.error);
      console.warn('errorCorrelationId', errorCorrelationId);
      mixpanelTrack('  (stripe payload OTP)', {
        errorCorrelationId,
        errorReason: payload.error.message,
      });
      return { error: payload.error, hadItemsToPurchase: true, success: false };
    }

    // Or should this be before .confirm?
    await createPaymentRequest({
      priceIds: oneTimeItems.map(item => item.id),
      workflowReferenceId,
    });

    return { hadItemsToPurchase: true, success: true };
  };

  const purchaseSubscriptions = async (paymentMethodId: string, personId: string): Promise<PaymentSuccess> => {
    const priceIds = cart
      .filter(isSubscriptionPrice)
      .map(p => p.stripePriceId)
      .filter(removeNullish);

    if (priceIds.length === 0) {
      return { hadItemsToPurchase: false, success: true };
    }
    if (!stripe || !elements) {
      console.warn('errorCorrelationId', errorCorrelationId);
      mixpanelTrack('Error in checkout flow (no stripe instance Subscription)', { errorCorrelationId });
      logError(new Error('No stripe instance'), { errorCorrelationId, level: Severity.Fatal, tags: { flow: 'purchase' } });
      return { error: 'No stripe instance', hadItemsToPurchase: true, success: false };
    }

    if (!creator.stripe_account_id) {
      console.warn('errorCorrelationId', errorCorrelationId);
      mixpanelTrack('Error in checkout flow (expert is not in stripe)', {
        creator: creatorSlug,
        errorCorrelationId,
      });
      logError(new Error('Expert is not in stripe'), { errorCorrelationId, level: Severity.Fatal, tags: { flow: 'purchase' } });
      return { error: 'Could not create payment', hadItemsToPurchase: true, success: false };
    }

    const { error: createSubscriptionError } = await createSubscriptionsFn({
      customTermsId: customTermsId || null,
      discountCodeId: appliedDiscountCode?.id || null,
      gaClientId,
      localCurrency,
      paymentMethodId,
      personId,
      priceIds,
      utmParams: {
        ...getUTMParams(router.query),
      },
      workflowReferenceId,
    });

    if (createSubscriptionError) {
      console.warn('errorCorrelationId', errorCorrelationId);
      mixpanelTrack('Error in checkout flow (createSubscriptionError)', {
        correlationId: createSubscriptionError.correlationId,
        errorCorrelationId,
        errors: createSubscriptionError.errors,
      });
      return { error: 'Error creating subscription', hadItemsToPurchase: true, success: false };
    }

    return { hadItemsToPurchase: true, success: true };
  };

  const purchaseFreeItems = async (personId: string): Promise<PaymentSuccess> => {
    const freeItems = cart.filter(isFreePrice);
    let hasError = false;
    await Bluebird.mapSeries(freeItems, async item => {
      const { error: buyFreeProductError } = await buyFreeProductFn({
        gaClientId: gaClientId as string,
        personId,
        priceId: item.id,
        productId: item.product.id,
        sessionDuration,
        sessionTime,
        source: Array.isArray(router.query.source) ? router.query.source[0] : router.query.source,
        utmParams: {
          ...getUTMParams(router.query),
        },
        workflowReferenceId,
      });
      if (buyFreeProductError) {
        hasError = true;
        console.warn('errorCorrelationId', errorCorrelationId);
        mixpanelTrack('Error in checkout flow (failed to purchase free products)', {
          correlationId: buyFreeProductError.correlationId,
          errorCorrelationId,
          errors: buyFreeProductError.errors,
          freeProducts: freeItems.map(i => i?.product?.title).toString(),
        });
      }
    });
    if (hasError) {
      return { error: 'Failed to purchase free products', hadItemsToPurchase: true, success: false };
    }
    return { hadItemsToPurchase: freeItems.length > 0, success: true };
  };

  const getPersonId = async (): Promise<string> => {
    if (user && !user.isAnonymous) {
      return user.id;
    }
    if (existingPersonId) {
      return existingPersonId;
    }
    const { data } = await findOrCreatePerson({
      creatorSlug,
      identifier: email,
      name: name || paymentMethodEvent?.payerName,
    });
    if (data?.token) {
      setUserByToken(data.token);
    }
    if (data?.person?.id) {
      return data.person.id;
    }
    throw new Error('Failed to find or create person');
  };

  const handleOnlyFreeSubmit = async (): Promise<void> => {
    setProcessing(true);
    try {
      mixpanelTrack('Purchasing only free products', {
        productsInCart: cart.map(p => p?.product?.title).toString(),
      });

      const personId = await getPersonId();

      ShoppingCart.setUser({ id: personId });
      // Delay allows cart to be created on backend before purchasing
      await Bluebird.delay(1000);

      await Bluebird.mapSeries(cart, async price => {
        await buyFreeProductFn({
          gaClientId: gaClientId as string,
          personId,
          priceId: price.id,
          productId: price.product.id,
          sessionDuration,
          sessionTime,
          source: Array.isArray(router.query.source) ? router.query.source[0] : router.query.source,
          utmParams: {
            ...getUTMParams(router.query),
          },
          workflowReferenceId,
        });
      });

      const purchasedCart = await ShoppingCart.purchaseCart({ discountCode: appliedDiscountCode?.code });
      setIsRouting(true);
      if (purchasedCart) {
        router.push(
          getUrl(IDENTIFIERS.PURCHASE_CONFIRMATION_CART, {
            cartId: purchasedCart.id,
            creatorSlug,
            isAchPayment: isAchPaymentEnabled,
            isSaaSProduct,
            tier: tierTitle,
            ...router.query,
          }),
        );
      } else {
        // In the unlikely event that no purchased cart was returned (activeCreatorSlug not set).
        router.push(getUrl(IDENTIFIERS.USER_PROFILE, { creatorSlug }));
      }
    } catch (err) {
      console.warn('errorCorrelationId', errorCorrelationId);
      mixpanelTrack('Error in checkout flow (in catch)', {
        creator: creatorSlug,
        errorCorrelationId,
        message: err instanceof Error ? err.message : 'Unknown',
      });
      setProcessing(false);
      logError(err, { errorCorrelationId, level: Severity.Fatal, tags: { flow: 'purchase' } });
      setError('Could not complete payment. Please contact support.');
    }
    setProcessing(false);
  };

  const getPaymentMethodId = async (personId: string, externalPaymentMethodId: string | undefined): Promise<string | null> => {
    if (!stripe || !elements) {
      throw new Error(`Stripe not initialized: stripe=${Boolean(stripe)} elements=${Boolean(elements)}`);
    }

    const systemHealthMonitor = new SystemHealthMonitor('cart-submit');

    let paymentMethodId: string | undefined | null = externalPaymentMethodId;

    if (isCardPaymentEnabled) {
      if (!paymentMethodId && useDefaultPaymentMethod && defaultPaymentMethod?.type === 'card') {
        paymentMethodId = defaultPaymentMethod.id;
      }

      if (!paymentMethodId) {
        const card = elements?.getElement(CardNumberElement);
        if (!card) {
          throw new Error('Failed to get card from stripe');
        }

        const paymentMethodData = await stripe.createPaymentMethod({
          billing_details: {
            name: payerName || '',
          },
          card,
          type: 'card',
        });

        const { error: stripeError, paymentMethod } = paymentMethodData;

        if (stripeError) {
          console.warn('errorCorrelationId', errorCorrelationId);
          mixpanelTrack('Error in checkout flow (stripe error)', {
            errorCorrelationId,
            errorReason: stripeError.message,
          });
          setError('Could not complete payment');
          systemHealthMonitor.finish(stripeError);
          return null;
        }

        if (!paymentMethod) {
          console.warn('errorCorrelationId', errorCorrelationId);
          mixpanelTrack('Error in checkout flow (no payment method)', { errorCorrelationId });
          const noPaymentMethodError = new Error('No payment method available');
          logError(noPaymentMethodError, { errorCorrelationId, level: Severity.Fatal, tags: { flow: 'purchase' } });
          setError('Failed to process payment method. Please try another card or contact support@noodle.shop.');
          systemHealthMonitor.finish(noPaymentMethodError);
          return null;
        }

        paymentMethodId = paymentMethod.id;
      }
    } else if (isAchPaymentEnabled) {
      if (!paymentMethodId && useDefaultPaymentMethod && defaultPaymentMethod?.type === 'us_bank_account') {
        paymentMethodId = defaultPaymentMethod.id;
      }
      if (!paymentMethodId) {
        const { data: setupIntent } = await createSetupIntentFn({ creatorId, personId });
        const clientSecret = setupIntent?.client_secret;
        if (!clientSecret) {
          throw new Error('Failed to create setup intent');
        }
        const bankSetupResponse = await stripe.collectBankAccountForSetup({
          clientSecret,
          expand: ['payment_method'],
          params: {
            payment_method_data: {
              billing_details: {
                email: stripeHelpers.makeTestEmail(email),
                name: payerName,
              },
            },
            payment_method_type: 'us_bank_account',
          },
        });

        const confirmBankSetupResponse = await stripe.confirmUsBankAccountSetup(clientSecret);
        const debugInfo = {
          nextAction: confirmBankSetupResponse.setupIntent?.next_action,
          setupIntentId: confirmBankSetupResponse.setupIntent?.id,
          setupIntentStatus: confirmBankSetupResponse.setupIntent?.status,
        };

        if (confirmBankSetupResponse.setupIntent?.status === 'succeeded') {
          paymentMethodId = stripeHelpers.getPaymentMethodId(bankSetupResponse.setupIntent?.payment_method);
        } else if (confirmBankSetupResponse.setupIntent?.next_action?.type === 'verify_with_microdeposits') {
          router.push(getUrl(IDENTIFIERS.USER_PROFILE_EDIT, { creatorSlug }));
        } else if (confirmBankSetupResponse.error) {
          console.warn('Failed bank account setup in an unexpected way', debugInfo);
          console.warn(confirmBankSetupResponse.error);
          logError(confirmBankSetupResponse.error, debugInfo);
          setError('Failed bank account setup');
        } else {
          console.warn('Failed bank account setup in an unexpected way', debugInfo);
          logError(new Error('Failed bank account setup in an unexpected way'), debugInfo);
          setError('Failed bank account setup');
        }
      }
    }
    if (creator?.id && paymentMethodId) {
      try {
        await attachNewCard({
          creatorId: creator.id,
          isDefaultPaymentMethod: true,
          paymentMethodId,
        });
      } catch (attachNewCardError) {
        const asApiError = ApiError.create(attachNewCardError);
        const reason = asApiError?.errors && asApiError?.errors.length > 0 ? asApiError.errors[0] : asApiError.message;
        console.log('errorCorrelationId', errorCorrelationId);
        mixpanelTrack('Error attaching card', { errorCorrelationId, paymentMethodId, reason });
      }
    }
    return paymentMethodId || null;
  };

  const handleSubmit = async (ev?: MouseEvent<HTMLButtonElement> | null, externalPaymentMethodId?: string): Promise<boolean> => {
    let success = false;
    const systemHealthMonitor = new SystemHealthMonitor('cart-submit');
    try {
      if (ev) {
        ev.preventDefault();
      }
      setProcessing(true);
      setError(null);
      const personId = await getPersonId();
      ShoppingCart.setUser({ id: personId });
      if (!stripe || !elements) {
        throw new Error(`Stripe not initialized: stripe=${Boolean(stripe)} elements=${Boolean(elements)}`);
      }

      const paymentMethodId = await getPaymentMethodId(personId, externalPaymentMethodId);

      if (!paymentMethodId) {
        return false;
      }

      const { hadItemsToPurchase: hadOtpItemsToPurchase, success: otpSuccess } = await purchaseOneTimeItems(paymentMethodId, personId);
      const { hadItemsToPurchase: hadSubscriptionItemsToPurchase, success: subscriptionSuccess } = await purchaseSubscriptions(
        paymentMethodId,
        personId,
      );
      const { hadItemsToPurchase: hadFreeItemsToPurchase, success: freeSuccess } = await purchaseFreeItems(personId);
      if (
        (hadOtpItemsToPurchase ? otpSuccess : true)
        && (hadSubscriptionItemsToPurchase ? subscriptionSuccess : true)
        && (hadFreeItemsToPurchase ? freeSuccess : true)
      ) {
        setSucceeded(true);
        const primaryProductId = getPrimaryProductId(cart.map(c => c.product));
        if (primaryProductId) {
          // The actual creation is based on the webhook from stripe, Wait till the personProduct has been created in the DB.
          try {
            await retry(
              async retryFn => {
                try {
                  const { error: getPersonProductError } = await getPersonProductsByProductIdFn({
                    personId,
                    productId: primaryProductId,
                  });
                  if (getPersonProductError) {
                    throw getPersonProductError;
                  }
                } catch (err) {
                  retryFn(err);
                }
              },
              {
                factor: 1,
                maxTimeout: 1 * 1000,
                retries: 20,
              },
            );
          } catch (e) {
            const asApiError = ApiError.create(e);
            console.error('errorCorrelationId', errorCorrelationId);
            logError(new Error('Failed to get person product in purchase flow'), {
              correlationId: asApiError.correlationId,
              errorCorrelationId,
              errors: asApiError.errors,
              level: Severity.Fatal,
              message: asApiError.message,
              tags: {
                flow: 'purchase',
              },
            });
            mixpanelTrack('Error in checkout flow (failed to get person product)', {
              correlationId: asApiError.correlationId,
              errorCorrelationId,
              errors: asApiError.errors,
              message: asApiError.message,
            });
          }
        } else {
          console.log('errorCorrelationId', errorCorrelationId);
          mixpanelTrack('Error in checkout flow (no products in cart)', { errorCorrelationId });
          logError(new Error('No purchased products in cart'), {
            errorCorrelationId,
            freeSuccess,
            hadFreeItemsToPurchase,
            hadOtpItemsToPurchase,
            hadSubscriptionItemsToPurchase,
            level: Severity.Fatal,
            otpSuccess,
            priceIds: cart.map(i => i.id).toString(),
            productIds: cart.map(i => i?.product?.id).toString(),
            subscriptionSuccess,
            tags: { flow: 'purchase' },
          });
        }
        const purchasedCart = await ShoppingCart.purchaseCart({
          discountCode: appliedDiscountCode?.code,
        });
        setIsRouting(true);
        if (purchasedCart) {
          mixpanelTrack('Completed purchase', {
            productsBought: cart.map(i => i?.product?.title).toString(),
          });
          router.push(
            getUrl(IDENTIFIERS.PURCHASE_CONFIRMATION_CART, {
              cartId: purchasedCart.id,
              creatorSlug,
              isAchPayment: isAchPaymentEnabled,
              isSaaSProduct,
              tier: tierTitle,
            }),
          );
        } else {
          // In the unlikely event that no purchased cart was returned (activeCreatorSlug not set).
          router.push(getUrl(IDENTIFIERS.USER_PROFILE, { creatorSlug }));
        }
        success = true;
      } else {
        console.log('errorCorrelationId', errorCorrelationId);
        mixpanelTrack('Error in checkout flow (failed purchase endpoints)', {
          creator: creatorSlug,
          errorCorrelationId,
          freeItemSuccess: freeSuccess,
          hadFreeItemToPurchase: hadFreeItemsToPurchase,
          hadOTPItemsToPurchase: hadOtpItemsToPurchase,
          hadSubItemsToPurchase: hadSubscriptionItemsToPurchase,
          otpItemSuccess: otpSuccess,
          subscriptionItemSuccess: subscriptionSuccess,
        });
        setError('Could not complete payment');
      }
      setProcessing(false);
    } catch (err) {
      console.log('errorCorrelationId', errorCorrelationId);
      mixpanelTrack('Error in checkout flow (in catch)', {
        creator: creatorSlug,
        errorCorrelationId,
        message: err instanceof Error ? err.message : 'Unknown',
      });
      setProcessing(false);
      logError(err, { errorCorrelationId, level: Severity.Fatal, tags: { flow: 'purchase' } });
      setError('Could not complete payment. Please contact support.');
    }
    systemHealthMonitor.finish(success ? undefined : new Error('Purchase was unsuccessful'));
    return success;
  };

  useEffect(() => {
    if (paymentRequest && stripe) {
      paymentRequest.on('paymentmethod', async ev => {
        if (ev.payerEmail && !email) {
          setEmail(ev.payerEmail);
        }
        if (ev.payerName && !payerName) {
          setPayerName(ev.payerName);
        }
        if (!paymentMethodEvent) {
          setPaymentMethodEvent(ev);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentRequest, stripe, creatorSlug]);

  useEffect(() => {
    const handleEvent = async (): Promise<void> => {
      if (paymentMethodEvent) {
        const success = await handleSubmit(null, paymentMethodEvent.paymentMethod.id);
        paymentMethodEvent.complete(success ? 'success' : 'fail');
        if (!success) {
          setPaymentMethodEvent(null);
        } else {
          mixpanelTrack(`Purchased with ${paymentMethodEvent.walletName}`);
        }
      }
    };
    handleEvent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentMethodEvent]);

  if (isRouting) {
    return <ProgressIndicator isCentered />;
  }

  return (
    <div className={classes.checkoutFormContainer}>
      {sessionTime && onChangeBookingTime && (
        <>
          <div className={classes.selectedDateAndTime}>
            <p className="caption-bold">
              Selected date and time
              <Buttons isWrapper className={classes.selectedDateAndTimeEditButton} onClick={() => onChangeBookingTime()}>
                Change
              </Buttons>
            </p>
            <p className="body-sm">{format(new Date(sessionTime), "EEEE, MMMM d, yyyy 'at' h:mm aa")}</p>
          </div>
          <Spacer />
        </>
      )}
      {error && (
        <div className={classes.errorMessage}>
          We were unable to process your order. Please try a different payment method or <a href={'mailto:support@noodle.shop'}>contact support</a>.
        </div>
      )}
      {paymentRequest && !onlyFreeProducts && isCardPaymentEnabled && (
        <>
          <Spacer size={24} />
          <div className={classes.walletButton}>
            <PaymentRequestButtonElement options={{ paymentRequest }} />
          </div>
          <Spacer size={24} />
          <div className={classes.orPayWithCard}>
            <hr />
            <small>Or pay with card</small>
          </div>
        </>
      )}
      {isAchPaymentEnabled && !isCardPaymentEnabled && (
        <>
          <h2 className="heading-sm">Pay by ACH</h2>
          <Spacer size={12} />
        </>
      )}
      <PersonalInfo
        email={email}
        hasInitialEmail={hasInitialEmail}
        hasInitialName={hasInitialName}
        isRouting={isRouting}
        name={name}
        setEmail={setEmail}
        setEmailError={setEmailError}
        setName={setName}
      />

      <div className={classes.divider} />
      {isFetchingPaymentMethod
        ? (
          <ProgressIndicator isCentered />
        )
        : (
          <>
            <span className={classNames('body-sm-bold', classes.paymentInfo)}>Payment Information</span>
            <Spacer size={16} />
            <DefaultPaymentMethod
              creatorSlug={creatorSlug}
              defaultPaymentMethod={defaultPaymentMethod}
              isAchPaymentEnabled={isAchPaymentEnabled}
              isCardPaymentEnabled={isCardPaymentEnabled}
              unverifiedPaymentMethods={unverifiedPaymentMethods}
              useDifferentPaymentMethod={useDifferentPaymentMethod}
              userHasCustomerStripeId={userHasCustomerStripeId}
            />
            {isAddNewPaymentMethodButtonShown && (
              <>
                <Buttons className={classes.newPaymentButton} isWrapper onClick={() => setUseDifferentPaymentMethod(true)}>
                  <PlusCircle weight="fill" size={24} color="var(--color-noodle)" />
                  <span>Use a different payment method</span>
                </Buttons>
                <Spacer size={12} />
              </>
            )}
            {showCreditCardForm && isCardPaymentEnabled && (
              <>
                {defaultPaymentMethod?.card && (
                  <>
                    <Buttons className={classes.newPaymentButton} isWrapper onClick={() => setUseDifferentPaymentMethod(false)}>
                      <ArrowCircleLeft weight="fill" size={24} color="var(--color-noodle)" />
                      <span>Use existing payment method</span>
                    </Buttons>
                    <Spacer size={24} />
                  </>
                )}
                <span className={classes.cardNumberLabel}>Card Number</span>
                <div className={classes.cardContainersWrapper}>
                  <div className={classes.cardNumberContainer}>
                    <CardNumberElement options={CARD_ELEMENT_OPTIONS} onChange={handleCardElementChange} />
                  </div>
                  <div className={classes.cardExtraInfoContainer}>
                    <div className={classes.cardExpirationContainer}>
                      <CardExpiryElement options={CARD_ELEMENT_OPTIONS} onChange={handleExpiryChange} />
                    </div>
                    <div className={classes.cardCvcContainer}>
                      <CardCvcElement options={CARD_ELEMENT_OPTIONS} onChange={handleCvcChange} />
                    </div>
                  </div>
                </div>
                <InputField
                  id="payerName"
                  label="Name on card"
                  name="payerName"
                  values={{ payerName }}
                  placeholder="Enter the name on card"
                  onChange={setPayerName}
                  autoComplete={'name'}
                  onFocus={() => setShouldPrefillPayerName(false)}
                />
              </>
            )}
          </>
        )}
      {showCreditCardForm && (
        <>
          <Spacer size={6} />
          <Buttons isExtraSmall isPrimary isWrapper onClick={() => setShowDiscountCodeInput(!showDiscountCodeInput)}>
            <Tag size={8} weight="fill" />
            Add discount code
          </Buttons>
          {showDiscountCodeInput && (
            <>
              <Spacer size={2} />
              <InputField
                hasFixedHeight={false}
                id="discountCode"
                name="discountCode"
                placeholder="XXX-XXXXXX"
                values={{ discountCode }}
                onChange={setDiscountCode}
              />
            </>
          )}
          <Spacer size={6} />
        </>
      )}

      {isAchPaymentEnabled && defaultPaymentMethod?.us_bank_account && useDifferentPaymentMethod && (
        <>
          <div className={classes.newPaymentButton}>
            <Buttons isWrapper onClick={() => setUseDifferentPaymentMethod(false)}>
              <ArrowCircleLeft weight="fill" size={24} color="var(--color-noodle)" />
            </Buttons>
            <span>Use existing payment method</span>
          </div>
          <Spacer size={24} />
        </>
      )}

      {isAchPaymentEnabled && (defaultPaymentMethod?.us_bank_account ? useDifferentPaymentMethod : true) && (
        <InputField
          id="bankName"
          label="Name on bank account"
          name="payerName"
          values={{ payerName }}
          placeholder="Enter the name on the bank account"
          onChange={setPayerName}
          autoComplete={'name'}
        />
      )}
      {appliedDiscountCode && !discountError && <span className={classes.discountApplied}>Discount applied!</span>}
      {discountError && <span className={classes.discountError}>{discountError}</span>}
      {!isRouting && (
        <>
          <Spacer size={12} />
          <Buttons
            isSecondary
            isLarge
            isFullWidth
            disabled={disableButton}
            onClick={onlyFreeProducts ? handleOnlyFreeSubmit : handleSubmit}
            isFetching={processing}
          >
            {processing ? 'Processing...' : handleSubmitText()}
          </Buttons>
          <Spacer size={12} />
        </>
      )}
      {showCreditCardForm && <span className={classes.encryption}>Your payment details are protected by SSL encryption.</span>}
    </div>
  );
};

export default CartPaymentFormStep;
